import React from 'react'
import { TextField } from '@/components/ui/TextField'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { Button, ButtonText } from '@/components/ui/button'
import { UseMutationResult } from '@tanstack/react-query'
import useLogin from '@/services/api/auth/useLogin'

export default function LoginForm({
  onSuccess,
  verifyEmail = true,
}: {
  onSuccess?: () => {}
  verifyEmail?: boolean
}) {
  const schema = z.object({
    email: z.string().min(1, 'The email field is required.'),
    password: z.string().min(1, 'The password field is required.'),
  })

  const { register, setError, handleSubmit, formState, control } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const { login, loginStatus } = useLogin(setError)

  const email =
    useWatch({
      control: control,
      name: 'email',
    }) ?? ''

  const { errors } = formState

  async function handleLogin(values) {
    const { email, password } = values
    await login({ email, password, checkVerification: verifyEmail })
    onSuccess()
  }

  return (
    <>
      <form
        noValidate
        className="grid gap-3"
        onSubmit={handleSubmit(handleLogin)}
      >
        <div>
          <TextField
            statusText={errors?.email?.message}
            status={errors?.email ? 'error' : ''}
            large
          >
            <TextField.Label>Email</TextField.Label>
            <TextField.Input
              placeholder="example@gmail.com"
              {...register('email')}
            />
          </TextField>
        </div>

        <div>
          <TextField
            statusText={errors?.password?.message}
            status={errors?.password ? 'error' : ''}
          >
            <TextField.Label className="flex justify-between">
              <span>Password</span>

              <Link
                className="link pr-1 underline-offset-4"
                to={`/forgot-password?email=${encodeURIComponent(email)}`}
              >
                Forgot your password?
              </Link>
            </TextField.Label>
            <TextField.PasswordInput
              id="password-input"
              {...register('password')}
            />
          </TextField>

          {/* {loginStatus.status === 'error' && (
        <p className="mt-3 flex items-center gap-1 text-sm text-txt-error">
          <ExclamationTriangleIcon className="size-4" />
          <span>{loginStatus.error.response.data.message}</span>
        </p>
      )} */}
        </div>
        <button className="hidden" type="submit">
          submit
        </button>
      </form>
      <div className="mt-7">
        <Button
          type="submit"
          onClick={handleSubmit(handleLogin)}
          loading={loginStatus.status === 'pending'}
          size="lg"
          className="w-full"
        >
          <ButtonText>
            {loginStatus.status === 'pending' ? 'Please wait' : 'Sign In'}
          </ButtonText>
        </Button>
        <p className="mt-2 text-sm text-txt-secondary">
          Don&apos;t have an account? <Link to="/register">Sign up</Link>
        </p>
      </div>
    </>
  )
}
